import { afterEach, beforeAll, describe, expect, it, jest } from '@jest/globals';
import {
  defaultFeatures,
  type AppConfig,
  type FeatureName,
  type Features,
  type PackageSettings,
} from '@ms-cloudpack/common-types';
import { writeJson } from '@ms-cloudpack/json-utilities';
import { createTestFileStructure, removeTempDir } from '@ms-cloudpack/test-utilities';
import path from 'path';
import { readAppConfig } from './readAppConfig.js';
import { pathToFileURL } from 'url';
import { dedent } from 'ts-dedent';

describe('readAppConfig', () => {
  let testPath: string | undefined;
  let consoleWarnMock: jest.SpiedFunction<typeof console.warn>;
  let consoleErrorMock: jest.SpiedFunction<typeof console.error>;
  /** Set this to allow `console.warn()` (otherwise it will throw) */
  let expectConsoleWarn = false;
  /** Set this to allow `console.error()` (otherwise it will throw) */
  let expectConsoleError = false;

  async function createBasicFixture(appConfig: AppConfig) {
    testPath = await createTestFileStructure({
      'cloudpack.config.json': appConfig,
    });

    return testPath;
  }

  async function createMonorepoFixture(appConfig: AppConfig) {
    testPath = await createTestFileStructure(
      {
        'package.json': { name: 'repo', version: '1.0.0' },
        'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { scripts: '*' } },
        'apps/app/cloudpack.config.json': appConfig,
        'scripts/package.json': { name: 'scripts', version: '1.0.0' },
      },
      {
        links: {
          'node_modules/app': 'apps/app',
          'node_modules/scripts': 'scripts',
        },
      },
    );

    return {
      appPath: path.join(testPath, 'apps/app'),
      scriptsPath: path.join(testPath, 'scripts'),
    };
  }

  beforeAll(() => {
    consoleWarnMock = jest.spyOn(console, 'warn').mockImplementation(() => {
      if (!expectConsoleWarn) {
        throw new Error('Unexpected console.warn()');
      }
    });
    consoleErrorMock = jest.spyOn(console, 'error').mockImplementation(() => {
      if (!expectConsoleError) {
        throw new Error('Unexpected console.error()');
      }
    });
  });

  afterEach(async () => {
    consoleWarnMock.mockClear();
    consoleErrorMock.mockClear();
    expectConsoleWarn = false;
    expectConsoleError = false;
    testPath && (await removeTempDir(testPath));
    testPath = undefined;
  });

  it('returns an empty object if the config file does not exist', async () => {
    const result = await readAppConfig('/does/not/exist');
    expect(result.appConfig).toEqual({});
  });

  it("throws an error if config file can't be parsed", async () => {
    testPath = await createTestFileStructure({
      'package.json': {},
      'cloudpack.config.json': '{',
    });

    await expect(() => readAppConfig(testPath || '')).rejects.toThrow(
      /^Error parsing JSON from .*?cloudpack\.config\.json: /,
    );
  });

  it('returns the user config directly if there is no extended config', async () => {
    const config: AppConfig = {
      helpMessage: 'Here is the app specific help message',
    };
    const appPath = await createBasicFixture(config);

    expect((await readAppConfig(appPath)).appConfig).toEqual(config);
  });

  // Basic config resolution test (more coverage in resolveParentConfig.test.ts)
  it('reads and merges an extended JSON config from a relative path', async () => {
    const rootConfig: AppConfig = {
      helpMessage: 'Here is the root config help message',
      patchFilesGlobPattern: 'patches/*.patch',
      features: {
        enqueueDependencies: true,
      },
    };

    const { appPath, scriptsPath } = await createMonorepoFixture({
      extends: '../../scripts/cloudpack.root.json',
    });
    await writeJson(path.join(scriptsPath, 'cloudpack.root.json'), rootConfig);

    const { appConfig: result } = await readAppConfig(appPath);

    expect(result).toEqual(rootConfig);
  });

  it('reads and merges an extended CJS config referenced from a JSON file', async () => {
    const rootConfig: AppConfig = {
      helpMessage: 'Here is the root config help message',
      patchFilesGlobPattern: 'patches/*.patch',
      features: {
        enqueueDependencies: true,
      },
    };

    testPath = await createTestFileStructure({
      'package.json': { name: 'app', version: '1.0.0' },
      'cloudpack.config.json': { extends: './cloudpack.root.js' },
      // this implicitly must be CJS because the package.json doesn't say type: module
      'cloudpack.root.js': `module.exports = ${JSON.stringify(rootConfig)};`,
    });

    const { appConfig: result } = await readAppConfig(testPath);
    expect(result).toEqual(rootConfig);
  });

  it('reads and merges an extended ESM config referenced from a JSON file', async () => {
    const rootConfig: AppConfig = {
      helpMessage: 'Here is the root config help message',
      patchFilesGlobPattern: 'patches/*.patch',
      features: {
        enqueueDependencies: true,
      },
    };

    testPath = await createTestFileStructure({
      'package.json': { name: 'app', version: '1.0.0', type: 'module' },
      'cloudpack.config.json': { extends: './cloudpack.root.js' },
      'cloudpack.root.js': `export default ${JSON.stringify(rootConfig)};`,
    });

    const { appConfig: result } = await readAppConfig(testPath);
    expect(result).toEqual(rootConfig);
  });

  it('reads and merges extended JS and JSON configs referenced from a JS file', async () => {
    const cjsConfig: AppConfig = {
      helpMessage: 'Here is the root config help message',
    };
    const jsonConfig: AppConfig = {
      patchFilesGlobPattern: 'patches/*.patch',
    };
    const appConfig: AppConfig = {
      extends: ['./cloudpack.root.cjs', './cloudpack.root.json'],
      features: { enqueueDependencies: true },
    };

    testPath = await createTestFileStructure({
      'package.json': { name: 'app', version: '1.0.0', type: 'module' },
      'cloudpack.config.js': `export default ${JSON.stringify(appConfig)};`,
      'cloudpack.root.cjs': `module.exports = ${JSON.stringify(cjsConfig)};`,
      'cloudpack.root.json': jsonConfig,
    });

    const { appConfig: result } = await readAppConfig(testPath);
    expect(result).toEqual({ ...cjsConfig, ...jsonConfig, features: appConfig.features });
  });

  // Basic config resolution test (more coverage in resolveParentConfig.test.ts)
  it('resolves a package name to the extended config', async () => {
    const rootConfig: AppConfig = {
      helpMessage: 'Here is the root config help message',
      patchFilesGlobPattern: 'patches/*.patch',
      features: {
        enqueueDependencies: true,
      },
    };

    const { appPath, scriptsPath } = await createMonorepoFixture({
      extends: 'scripts/cloudpack.root.json',
    });
    await writeJson(path.join(scriptsPath, 'cloudpack.root.json'), rootConfig);

    const { appConfig: result } = await readAppConfig(appPath);

    expect(result).toEqual(rootConfig);
  });

  // This just verifies that the exception propagates (more coverage in resolveParentConfig.test.ts)
  it('throws if extended config path is invalid', async () => {
    const appPath = await createBasicFixture({
      extends: './nope.json',
    });

    await expect(() => readAppConfig(appPath)).rejects.toThrow(
      `Could not resolve "extends": "./nope.json":\nCannot find module '${path.join(appPath, 'nope.json')}' ` +
        `imported from ${path.join(appPath, 'cloudpack.config.json')}`,
    );
  });

  it('merges the user config and extended configs recursively', async () => {
    const config: AppConfig = {
      extends: 'scripts/cloudpack.mid.json',
      helpMessage: 'Here is the app specific help message',
      features: {
        verifyExports: true,
      },
      packageSettings: [
        { match: 'app', exports: './app.js' },
        { match: 'stuff', exports: './stuff.js' },
      ],
    };

    const midConfig: AppConfig = {
      extends: './cloudpack.root.json',
      helpMessage: 'Here is the mid level help message',
      remoteCache: {
        container: 'my-container',
        storageAccount: 'my-storage-account',
      },
      features: {
        optimizeDependencies: true,
      },
      packageSettings: [{ match: 'mid', exports: './mid.js' }],
    };

    const rootConfig: AppConfig = {
      helpMessage: 'Here is the root config help message',
      patchFilesGlobPattern: 'patches/*.patch',
      features: {
        enqueueDependencies: true,
      },
      packageSettings: [
        { match: 'root1', exports: './root1.js' },
        { match: 'root2', exports: './root2.js' },
      ],
    };

    const { appPath, scriptsPath } = await createMonorepoFixture(config);
    await writeJson(path.join(scriptsPath, 'cloudpack.mid.json'), midConfig);
    await writeJson(path.join(scriptsPath, 'cloudpack.root.json'), rootConfig);

    const { appConfig: result } = await readAppConfig(appPath);

    expect(result).toEqual({
      helpMessage: 'Here is the app specific help message',
      patchFilesGlobPattern: 'patches/*.patch',
      remoteCache: {
        container: 'my-container',
        storageAccount: 'my-storage-account',
      },
      features: {
        enqueueDependencies: true,
        optimizeDependencies: true,
        verifyExports: true,
      },
      packageSettings: [
        { match: 'root1', exports: './root1.js' },
        { match: 'root2', exports: './root2.js' },
        { match: 'mid', exports: './mid.js' },
        { match: 'app', exports: './app.js' },
        { match: 'stuff', exports: './stuff.js' },
      ],
    });
  });

  it('resolves an array of extended configs', async () => {
    const config: AppConfig = {
      extends: ['scripts/cloudpack.root.json', 'scripts/cloudpack.mid.json'],
      helpMessage: 'Here is the app specific help message',
      features: {
        verifyExports: true,
      },
    };

    const midConfig: AppConfig = {
      helpMessage: 'Here is the mid level help message',
      remoteCache: {
        container: 'my-container',
        storageAccount: 'my-storage-account',
      },
      features: {
        optimizeDependencies: true,
      },
    };

    const rootConfig: AppConfig = {
      helpMessage: 'Here is the root config help message',
      patchFilesGlobPattern: 'patches/*.patch',
      features: {
        enqueueDependencies: true,
      },
    };

    const { appPath, scriptsPath } = await createMonorepoFixture(config);
    await writeJson(path.join(scriptsPath, 'cloudpack.mid.json'), midConfig);
    await writeJson(path.join(scriptsPath, 'cloudpack.root.json'), rootConfig);

    const { appConfig: result } = await readAppConfig(appPath);

    expect(result).toEqual({
      helpMessage: 'Here is the app specific help message',
      patchFilesGlobPattern: 'patches/*.patch',
      remoteCache: {
        container: 'my-container',
        storageAccount: 'my-storage-account',
      },
      features: {
        enqueueDependencies: true,
        optimizeDependencies: true,
        verifyExports: true,
      },
    });
  });

  it('handles devServer backwards compat', async () => {
    const appPath = await createBasicFixture({
      devServer: { port: 123 },
    });

    const { appConfig: result } = await readAppConfig(appPath);

    expect(result.server).toEqual({ port: 123 });
    /* eslint-disable etc/no-deprecated */
    expect(result.devServer).not.toBeDefined();
  });

  it('handles server.routes backwards compat', async () => {
    const appConfig: AppConfig = { routes: [{ match: '/foo', entry: './index.js' }] };
    const appPath = await createBasicFixture(appConfig);

    const { appConfig: result } = await readAppConfig(appPath);

    expect(result.server?.routes).not.toBeDefined();
    expect(result.routes).toEqual(appConfig.routes);
  });

  it('handles PackageSettings.bundlerType backwards compat', async () => {
    const appPath = await createBasicFixture({
      packageSettings: [{ match: 'foo', bundlerType: 'rollup' }],
    });

    const { appConfig: result } = await readAppConfig(appPath);

    expect(result.packageSettings).toEqual([{ match: 'foo', bundler: 'rollup' }]);
  });

  it('logs and throws if both devServer and server are present', async () => {
    const appPath = await createBasicFixture({
      devServer: { port: 123 },
      server: { port: 456 },
    });

    await expect(() => readAppConfig(appPath)).rejects.toThrow(
      'Cannot have both "devServer" and "server" in the config file. Use "server" only.',
    );
  });

  it('logs and throws if a packageSettings match has a negated name and a version', async () => {
    const appPath = await createBasicFixture({
      packageSettings: [{ match: { name: '!foo', version: '1.0.0' }, exports: './index.js' }],
    });

    expectConsoleError = true;
    await expect(() => readAppConfig(appPath)).rejects.toThrow(
      `Config file ${path.join(appPath, 'cloudpack.config.json')} has invalid settings (see above for details)`,
    );
    expect(consoleErrorMock).toHaveBeenCalledWith(
      'Invalid packageSettings match: Negated match name "!foo" cannot have a version ("1.0.0")',
    );
  });

  it('warns about and ignores invalid features from config file', async () => {
    const appPath = await createBasicFixture({
      features: { disableSourceMaps: true, ['foo' as FeatureName]: true },
    });

    expectConsoleWarn = true;
    const { appConfig: config } = await readAppConfig(appPath);
    expect(consoleWarnMock).toHaveBeenCalledWith(
      expect.stringMatching(/Unknown feature\(s\) found in .*cloudpack\.config\.json.*\n {2}foo/),
    );
    expect(config.features).toEqual({ disableSourceMaps: true });
  });

  it('merges extraFeatures', async () => {
    const features: Features = { disableSourceMaps: true, enqueueDependencies: false };
    const extraFeatures: Features = { enqueueDependencies: true, evaluateDynamicImports: false };

    const appPath = await createBasicFixture({ features });

    const { appConfig: config } = await readAppConfig(appPath, { extraFeatures });
    expect(config.features).toEqual({ ...features, ...extraFeatures });
  });

  it('includes extraPackageSettings at end of packageSettings', async () => {
    const packageSettings: PackageSettings[] = [{ match: 'foo', exports: { '.': './index.js' } }];
    // This simulates passing --bundler on the command line.
    const extraPackageSettings: PackageSettings[] = [{ match: '*', bundler: 'rollup' }];

    const appPath = await createBasicFixture({ packageSettings });

    const { appConfig: config } = await readAppConfig(appPath, { extraPackageSettings });

    expect(config).toEqual({
      packageSettings: [...packageSettings, ...extraPackageSettings],
    });
  });

  it('uses extraPackageSettings even if no config file', async () => {
    // This simulates passing --bundler on the command line.
    const extraPackageSettings: PackageSettings[] = [{ match: '*', bundler: 'rollup' }];

    const { appConfig: config } = await readAppConfig('/does/not/exist', { extraPackageSettings });

    expect(config).toEqual({
      packageSettings: extraPackageSettings,
    });
  });

  it('enables default features if requested', async () => {
    const appPath = await createBasicFixture({});

    const { appConfig: config } = await readAppConfig(appPath, { enableDefaultFeatures: true });
    expect(config.features).toEqual(defaultFeatures);
  });

  it('resolves bundler capabilities to full import specifiers', async () => {
    const appConfig: AppConfig = {
      bundlerCapabilitiesRegistry: {
        custom: './custom.js',
      },
      packageSettings: [
        {
          match: 'foo',
          bundlerCapabilities: {
            custom: {},
            alias: { bar: 'baz' },
          },
        },
      ],
    };

    testPath = await createTestFileStructure({
      'cloudpack.config.json': appConfig,
      'custom.js': '',
    });

    const { appConfig: result } = await readAppConfig(testPath);
    const customUrl = pathToFileURL(path.join(testPath, 'custom.js')).href;

    expect(result).toEqual({
      ...appConfig,
      bundlerCapabilitiesRegistry: {
        custom: customUrl,
      },
    });
  });

  it('supports bundler capabilities registered by parent configs', async () => {
    const parentConfig: AppConfig = {
      bundlerCapabilitiesRegistry: {
        custom: './custom.js',
      },
    };
    const appConfig: AppConfig = {
      extends: './parent/cloudpack.base.json',
      // this references a capability defined by the parent config
      packageSettings: [{ match: 'foo', bundlerCapabilities: { custom: {} } }],
    };

    testPath = await createTestFileStructure({
      'cloudpack.config.json': appConfig,
      'parent/cloudpack.base.json': parentConfig,
      'parent/custom.js': '',
    });

    const { appConfig: result } = await readAppConfig(testPath);
    const customUrl = pathToFileURL(path.join(testPath, 'parent/custom.js')).href;

    expect(result).toEqual({
      bundlerCapabilitiesRegistry: {
        custom: customUrl,
      },
      packageSettings: appConfig.packageSettings,
    });
  });

  it('errors on invalid bundlerCapabilitiesRegistry paths', async () => {
    const appPath = await createBasicFixture({
      bundlerCapabilitiesRegistry: {
        custom: './custom.js',
      },
    });

    await expect(() => readAppConfig(appPath)).rejects.toThrow(
      `Error resolving bundler capability  "./custom.js":\nCannot find module '${path.join(
        appPath,
        'custom.js',
      )}' imported from ${path.join(appPath, 'cloudpack.config.json')}`,
    );
  });

  it('allows trailing commas and comments', async () => {
    testPath = await createTestFileStructure({
      'cloudpack.config.json': dedent`{
          // comment
          "helpMessage": "help", // comment
        }`,
    });

    expect((await readAppConfig(testPath)).appConfig).toEqual({
      helpMessage: 'help',
    });
  });

  it('errors if multiple JS config files exist', async () => {
    testPath = await createTestFileStructure({
      'cloudpack.config.js': 'module.exports = { helpMessage: "JS config" };',
      'cloudpack.config.mjs': 'export default { helpMessage: "MJS config" };',
      'cloudpack.config.cjs': 'module.exports = { helpMessage: "CJS config" };',
    });

    await expect(() => readAppConfig(testPath ?? '')).rejects.toThrow(
      'Multiple Cloudpack config files found. Determine the correct one and delete the others:',
    );
  });

  it('errors if both JSON and JS configs exist', async () => {
    testPath = await createTestFileStructure({
      'cloudpack.config.json': { helpMessage: 'JSON config' },
      'cloudpack.config.mjs': 'export default { helpMessage: "JS config" };',
    });

    await expect(() => readAppConfig(testPath ?? '')).rejects.toThrow(
      'Multiple Cloudpack config files found. Determine the correct one and delete the others:',
    );
  });

  it('processes a valid MJS config file', async () => {
    testPath = await createTestFileStructure({
      'cloudpack.config.mjs': 'export default { helpMessage: "JS config" };',
    });

    const { appConfig: result } = await readAppConfig(testPath);

    expect(result).toEqual({ helpMessage: 'JS config' });
  });

  it('processes a valid CJS config file', async () => {
    testPath = await createTestFileStructure({
      'cloudpack.config.cjs': 'module.exports = { helpMessage: "CJS config" };',
    });

    const { appConfig: result } = await readAppConfig(testPath);

    expect(result).toEqual({ helpMessage: 'CJS config' });
  });

  // In Jest 30.4.1, jest-runtime throws a SyntaxError which aborts the test case
  // it('throws an error if CJS config file is invalid', async () => {
  //   testPath = await createTestFileStructure({
  //     'cloudpack.config.cjs': 'module.exports = invalid syntax;',
  //   });

  //   await expect(() => readAppConfig(testPath ?? '')).rejects.toThrow();
  // });

  it('merges extraServerConfig, overriding values from config file', async () => {
    const config: AppConfig = {
      server: { open: true, domain: 'example.com' },
    };
    testPath = await createTestFileStructure({
      'cloudpack.config.json': config,
    });

    const { appConfig: result } = await readAppConfig(testPath, {
      extraServerConfig: { open: false },
    });

    expect(result.server).toEqual({ ...config.server, open: false });
  });

  it('ignores undefined keys in extraServerConfig', async () => {
    const config: AppConfig = {
      server: { open: true, domain: 'example.com' },
    };
    testPath = await createTestFileStructure({
      'cloudpack.config.json': config,
    });

    const { appConfig: result } = await readAppConfig(testPath, {
      extraServerConfig: { open: undefined, showOverlay: 'never' },
    });

    expect(result.server).toEqual({ ...config.server, showOverlay: 'never' });
  });

  describe('with custom config filename', () => {
    it('reads config from custom JSON filename', async () => {
      const config: AppConfig = {
        helpMessage: 'Custom config help message',
        packageSettings: [{ match: 'test-package' }],
      };

      testPath = await createTestFileStructure({
        'my-custom-config.json': config,
      });

      const { appConfig: result } = await readAppConfig(testPath || '', { configFileName: 'my-custom-config.json' });
      expect(result).toEqual(config);
    });

    it('reads config from custom JS filename', async () => {
      testPath = await createTestFileStructure({
        'custom.config.js': dedent`
          module.exports = {
            helpMessage: 'JS config help message',
            packageSettings: [{ match: 'test-package' }],
          };
        `,
      });

      const { appConfig: result } = await readAppConfig(testPath || '', { configFileName: 'custom.config.js' });
      expect(result).toEqual({
        helpMessage: 'JS config help message',
        packageSettings: [{ match: 'test-package' }],
      });
    });

    it('throws error if custom config file does not exist', async () => {
      testPath = await createTestFileStructure({
        'cloudpack.config.json': { helpMessage: 'Default config' },
      });

      await expect(() => readAppConfig(testPath || '', { configFileName: 'non-existent.json' })).rejects.toThrow(
        'Config file not found: non-existent.json',
      );
    });

    it('throws error if custom config file cannot be parsed', async () => {
      testPath = await createTestFileStructure({
        'broken-config.json': '{invalid json',
      });

      await expect(() => readAppConfig(testPath || '', { configFileName: 'broken-config.json' })).rejects.toThrow(
        /Error parsing JSON/,
      );
    });

    // Environment variable expansion is already tested in the main tests,
    // and works the same way regardless of config filename

    it('merges extra settings with custom config', async () => {
      const config: AppConfig = {
        packageSettings: [{ match: 'package-a' }],
      };

      testPath = await createTestFileStructure({
        'custom.json': config,
      });

      const extraSettings: PackageSettings[] = [{ match: 'package-b', bundler: 'webpack' }];
      const { appConfig: result } = await readAppConfig(testPath || '', {
        configFileName: 'custom.json',
        extraPackageSettings: extraSettings,
      });

      expect(result.packageSettings).toEqual([{ match: 'package-a' }, { match: 'package-b', bundler: 'webpack' }]);
    });
  });
});
